import React, { useState, useEffect } from 'react';
import { Loader2, Rocket, ArrowRight } from 'lucide-react';
import { OnboardingWizard } from './OnboardingWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getApiKey = () => localStorage.getItem('customer_api_key');

/**
 * OnboardingGate - Wraps the dashboard and enforces onboarding completion
 * 
 * If wizard is not submitted, shows full-screen onboarding wizard
 * If wizard is submitted, renders children (dashboard)
 */
export const OnboardingGate = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [wizardStatus, setWizardStatus] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/wizard`, {
        headers: { 'X-API-Key': getApiKey() }
      });

      if (res.ok) {
        const data = await res.json();
        setWizardStatus(data.wizard_status);
        
        // If not submitted, show wizard
        if (data.wizard_status !== 'submitted' && data.wizard_status !== 'locked') {
          setShowWizard(true);
        }
      }
    } catch (err) {
      console.error('Failed to check onboarding status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = (result) => {
    setWizardStatus('submitted');
    setShowWizard(false);
    // Refresh the page to show dashboard
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show onboarding wizard if not complete
  if (showWizard) {
    return <OnboardingWizard onComplete={handleWizardComplete} />;
  }

  // Show entry gate banner if status is not_started or in_progress
  if (wizardStatus === 'not_started' || wizardStatus === 'in_progress') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-4">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Rocket className="w-10 h-10 text-blue-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Complete Your AI Agent Setup
          </h1>
          
          <p className="text-gray-600 mb-8 text-lg">
            Before your AI phone agent can go live, we need some information about your business. 
            This takes about 5-10 minutes.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3 text-left p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
              <div>
                <div className="font-medium text-gray-900">Business Information</div>
                <div className="text-sm text-gray-500">Name, location, hours</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-left p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</div>
              <div>
                <div className="font-medium text-gray-900">Call Handling Preferences</div>
                <div className="text-sm text-gray-500">What your AI should do</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-left p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">3</div>
              <div>
                <div className="font-medium text-gray-900">Brand Voice</div>
                <div className="text-sm text-gray-500">How your AI should sound</div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowWizard(true)}
            className="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            Start Setup
            <ArrowRight className="w-5 h-5" />
          </button>

          {wizardStatus === 'in_progress' && (
            <p className="mt-4 text-sm text-green-600 font-medium">
              ✓ You have progress saved. Continue where you left off!
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render children (dashboard) if onboarding is complete
  return children;
};

export default OnboardingGate;
